
import React, { useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  Mail,
  Phone,
  MessageSquare,
  Bug,
  Lightbulb,
  FileText,
  ChevronRight,
  ExternalLink,
  Headphones,
} from "lucide-react";

const LOGO_URL =
  "https://kwbgrjakobarqelunebd.supabase.co/storage/v1/object/public/pictures/Icon/3.png";

const LEGAL_LINKS = [
  {
    label: "Terms of Use",
    path: "/terms",
  },
  {
    label: "Privacy Policy",
    path: "/privacy",
  },
  {
    label: "Cookies",
    path: "/cookies",
  },
  {
    label: "POPIA",
    path: "/popia",
  },
  {
    label: "Refund and Cancellation",
    path: "/refund-and-cancellation",
  },
  {
    label: "Scoring and Funding-readiness disclaimer",
    path: "/scoring-and-funding-readiness-disclaimer",
  },
];

const FEEDBACK_LINKS = [
  {
    label: "General Feedback",
    description: "Share your experience or suggestions.",
    icon: MessageSquare,
    href: "https://docs.google.com/forms/d/e/1FAIpQLSci0fSi2GiOOaZv-CiHxeNc38dDXwP2pp92ZjR7RGqckTbk-A/viewform?usp=publish-editor",
  },
  {
    label: "Report a Bug",
    description: "Tell us about a technical issue.",
    icon: Bug,
    href: "https://docs.google.com/forms/d/e/1FAIpQLSdQewhzfH2Be_2JBuXoAXpt8_zRP1U4zWdJWWpO8GFZiMKwmA/viewform?usp=publish-editor",
  },
  {
    label: "Content Issue / Suggestion",
    description: "Help us improve Gwin's content.",
    icon: Lightbulb,
    href: "https://docs.google.com/forms/d/e/1FAIpQLScEpBYDxaLpQ1I7uvbhfgoAyCbxZqOg4ePL5P9wWhhl2Mk2zQ/viewform?usp=publish-editor",
  },
];

export default function ContactPage() {
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleBack = () => {
    const backTo = location.state?.backTo;

    if (
      typeof backTo === "string" &&
      backTo.startsWith("/") &&
      !backTo.startsWith("//") &&
      backTo !== location.pathname
    ) {
      navigate(backTo, { replace: true });
      return;
    }

    if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate("/");
    }
  };

  const legalBackState = {
    backTo: location.pathname,
    backLabel: "Back",
    replaceBack: true,
  };

  return (
    <div className="min-h-[100dvh] bg-[#F4F4F6] font-sans text-[#333333]">

      {/* HEADER */}
      <header
        className="sticky top-0 z-40 w-full border-b border-[#EFEFEF] bg-white"
        style={{
          paddingTop: "env(safe-area-inset-top, 0px)",
        }}
      >
        <div className="mx-auto flex w-full max-w-[430px] items-center justify-between px-5 py-3">

          <button
            type="button"
            onClick={handleBack}
            aria-label="Go back"
            className="
              flex h-11 w-11 items-center justify-center
              rounded-2xl bg-[#F4F4F6] text-[#333333]
              transition hover:bg-[#EBEBEE] active:scale-95
              focus-visible:outline-none focus-visible:ring-2
              focus-visible:ring-[#FF914D]
            "
          >
            <ArrowLeft size={21} strokeWidth={2.5} />
          </button>

          <img
            src={LOGO_URL}
            alt="Gwin"
            className="h-11 w-auto object-contain"
            loading="eager"
            decoding="async"
            draggable="false"
          />

          <div className="h-11 w-11" aria-hidden="true" />
        </div>
      </header>

      {/* MAIN CONTENT */}
      <main
        className="mx-auto w-full max-w-[430px] px-5 pt-7"
        style={{
          paddingBottom:
            "calc(36px + env(safe-area-inset-bottom, 0px))",
        }}
      >

        {/* PAGE INTRODUCTION */}
        <section className="mb-7">
          <div className="mb-3 inline-flex items-center gap-2 rounded-full bg-[#FFF0E5] px-3 py-1.5">
            <Headphones
              size={14}
              className="text-[#FF914D]"
            />

            <span className="text-[10px] font-black uppercase tracking-[0.14em] text-[#E9782F]">
              Gwin Support
            </span>
          </div>

          <h1 className="text-[30px] font-black leading-tight tracking-[-0.035em] text-[#111111]">
            Contact &amp;
            <br />
            <span className="text-[#FF914D]">
              Legal Notices
            </span>
          </h1>

          <p className="mt-3 max-w-[340px] text-[13px] font-medium leading-relaxed text-[#8A8A8A]">
            Need assistance, want to share feedback,
            or looking for important information?
            We're here to help.
          </p>
        </section>

        {/* CONTACT DETAILS */}
        <section className="mb-5 rounded-[24px] border border-[#EAEAEA] bg-white p-5 shadow-[0_4px_18px_rgba(0,0,0,0.035)]">

          <div className="mb-5 flex items-center gap-3">
            <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-[#FFF0E5]">
              <Headphones
                size={21}
                className="text-[#FF914D]"
                strokeWidth={2.3}
              />
            </div>

            <div>
              <h2 className="text-[18px] font-black text-[#111111]">
                Get in touch
              </h2>

              <p className="mt-0.5 text-[11px] font-medium text-[#8A8A8A]">
                Contact the Gwin team
              </p>
            </div>
          </div>

          <div className="space-y-3">

            {/* EMAIL */}
            <a
              href="mailto:office@gwin.co.za"
              className="
                flex min-w-0 items-center gap-3
                rounded-2xl border border-[#EEEEF0]
                bg-[#FAFAFB] p-3.5
                transition hover:border-[#FF914D]
                hover:bg-[#FFF8F3]
              "
            >
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-white">
                <Mail
                  size={19}
                  className="text-[#FF914D]"
                />
              </div>

              <div className="min-w-0 flex-1">
                <p className="text-[11px] font-semibold text-[#8A8A8A]">
                  Email us
                </p>

                <p className="break-all text-[13px] font-extrabold text-[#333333]">
                  office@gwin.co.za
                </p>
              </div>

              <ChevronRight
                size={18}
                className="shrink-0 text-[#B0B0B0]"
              />
            </a>

            {/* PHONE */}
            <a
              href="tel:+27721386570"
              className="
                flex items-center gap-3
                rounded-2xl border border-[#EEEEF0]
                bg-[#FAFAFB] p-3.5
                transition hover:border-[#FF914D]
                hover:bg-[#FFF8F3]
              "
            >
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-white">
                <Phone
                  size={19}
                  className="text-[#FF914D]"
                />
              </div>

              <div className="min-w-0 flex-1">
                <p className="text-[11px] font-semibold text-[#8A8A8A]">
                  Call us
                </p>

                <p className="text-[13px] font-extrabold text-[#333333]">
                  +27 72 138 6570
                </p>
              </div>

              <ChevronRight
                size={18}
                className="shrink-0 text-[#B0B0B0]"
              />
            </a>
          </div>

          <div className="mt-4 rounded-2xl bg-[#FFF6EF] px-4 py-3">
            <p className="text-[12px] font-semibold leading-relaxed text-[#8D6042]">
              We'll get back to you as soon as possible.
            </p>
          </div>
        </section>

        {/* FEEDBACK FORMS */}
        <section className="mb-5 rounded-[24px] border border-[#EAEAEA] bg-white p-5 shadow-[0_4px_18px_rgba(0,0,0,0.035)]">

          <div className="mb-4 flex items-center gap-3">
            <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-[#FFF0E5]">
              <MessageSquare
                size={21}
                className="text-[#FF914D]"
                strokeWidth={2.3}
              />
            </div>

            <div>
              <h2 className="text-[18px] font-black text-[#111111]">
                Feedback Forms
              </h2>

              <p className="mt-0.5 text-[11px] font-medium text-[#8A8A8A]">
                Help us improve Gwin
              </p>
            </div>
          </div>

          <p className="mb-4 text-[12px] font-medium leading-relaxed text-[#757575]">
            Choose the option that best matches your feedback.
          </p>

          <div className="space-y-3">
            {FEEDBACK_LINKS.map((item) => {
              const Icon = item.icon;

              return (
                <a
                  key={item.label}
                  href={item.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="
                    flex min-w-0 items-center gap-3
                    rounded-2xl border border-[#EEEEF0]
                    bg-[#FAFAFB] p-3.5
                    transition hover:border-[#FF914D]
                    hover:bg-[#FFF8F3]
                  "
                >
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-white">
                    <Icon
                      size={19}
                      className="text-[#FF914D]"
                    />
                  </div>

                  <div className="min-w-0 flex-1">
                    <p className="text-[13px] font-extrabold leading-snug text-[#333333]">
                      {item.label}
                    </p>

                    <p className="mt-1 text-[11px] font-medium leading-relaxed text-[#8A8A8A]">
                      {item.description}
                    </p>
                  </div>

                  <ExternalLink
                    size={17}
                    className="shrink-0 text-[#B0B0B0]"
                  />
                </a>
              );
            })}
          </div>
        </section>

        {/* LEGAL & POLICY INFORMATION */}
        <section className="rounded-[24px] border border-[#EAEAEA] bg-white p-5 shadow-[0_4px_18px_rgba(0,0,0,0.035)]">

          <div className="mb-4 flex items-center gap-3">
            <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-[#FFF0E5]">
              <FileText
                size={21}
                className="text-[#FF914D]"
                strokeWidth={2.3}
              />
            </div>

            <div>
              <h2 className="text-[18px] font-black text-[#111111]">
                Legal &amp; Policies
              </h2>

              <p className="mt-0.5 text-[11px] font-medium text-[#8A8A8A]">
                Important Gwin information
              </p>
            </div>
          </div>

          <p className="mb-4 text-[12px] font-medium leading-relaxed text-[#757575]">
            Review our policies, terms and important disclaimers.
          </p>

          <div className="overflow-hidden rounded-2xl border border-[#EEEEF0] bg-[#FAFAFB]">
            {LEGAL_LINKS.map((item, index) => (
              <Link
                key={item.path}
                to={item.path}
                replace
                state={legalBackState}
                className={[
                  "flex min-h-[52px] items-center justify-between gap-3 px-4 py-3.5 transition hover:bg-[#FFF6EF]",
                  index !== LEGAL_LINKS.length - 1
                    ? "border-b border-[#EEEEF0]"
                    : "",
                ].join(" ")}
              >
                <span className="min-w-0 text-[12px] font-bold leading-relaxed text-[#333333]">
                  {item.label}
                </span>

                <ChevronRight
                  size={17}
                  className="shrink-0 text-[#FF914D]"
                />
              </Link>
            ))}
          </div>
        </section>

        {/* FOOTER */}
        <div className="px-4 pb-4 pt-8 text-center">
          <p className="text-[11px] font-semibold text-[#A0A0A0]">
            Gwin · Made in Cape Town, South Africa
          </p>
        </div>
      </main>
    </div>
  );
}
